import { useCallback, useState } from 'react'

import { useUserContext } from '~config/user-context'
import { useClinic } from '~contexts/clinic-context'
import { useMyClinicAgentConversationsQuery } from '~graphql/generated/graphql'

const HAS_ASKED_STORAGE_PREFIX = 'chckup.askChckvet.hasAskedQuestion.v1'
const INTRO_DISMISSED_STORAGE_PREFIX = 'chckup.askChckvet.intro.dismissed.v1'

/**
 * Session fallback for when localStorage cannot be written. Dismissal itself
 * is persisted per user so the popover does not return on the next visit.
 */
let isIntroDismissedThisLoad = false

export type AskChckvetIntroPrompt = {
    isVisible: boolean
    /** Hide for good on this browser for the signed-in user. */
    dismiss: () => void
}

/**
 * "Have a question?" intro for Ask Chckvet. Shows until the user dismisses
 * it or asks a question. Either one retires it for later visits.
 *
 * "Has asked" is any Ask Chckvet conversation of theirs in this clinic. The
 * thread refetches `myClinicAgentConversations` after each send, so the
 * prompt disappears as soon as they ask. Once the server has confirmed it,
 * a per-user flag skips the lookup on later loads. Closing the popover
 * writes {@link INTRO_DISMISSED_STORAGE_PREFIX} so it stays closed even
 * when they have not asked yet.
 */
export function useAskChckvetIntroPrompt(args: {
    isEnabled: boolean
    isDockOpen: boolean
}): AskChckvetIntroPrompt {
    const { userId } = useUserContext()
    const { clinic } = useClinic()
    const clinicId = clinic?.id
    const [isDismissedThisLoad, setIsDismissedThisLoad] = useState(
        isIntroDismissedThisLoad,
    )
    const isDismissed = isDismissedThisLoad || readIntroDismissed(userId)

    const shouldCheckConversations = Boolean(
        args.isEnabled &&
        !isDismissed &&
        clinicId &&
        userId &&
        !readHasAskedQuestion(userId),
    )

    const { data } = useMyClinicAgentConversationsQuery({
        variables: { clinicId: clinicId ?? '', limit: 1 },
        skip: !shouldCheckConversations,
        fetchPolicy: 'cache-and-network',
        onCompleted: result => {
            if (result.myClinicAgentConversations.length > 0) {
                writeHasAskedQuestion(userId)
            }
        },
    })

    // Wait for the lookup so returning users never see a flash of the prompt.
    const hasNoConversations = data?.myClinicAgentConversations.length === 0

    const dismiss = useCallback(() => {
        isIntroDismissedThisLoad = true
        writeIntroDismissed(userId)
        setIsDismissedThisLoad(true)
    }, [userId])

    return {
        isVisible:
            shouldCheckConversations && hasNoConversations && !args.isDockOpen,
        dismiss,
    }
}

function hasAskedStorageKey(userId: string): string {
    return `${HAS_ASKED_STORAGE_PREFIX}:${userId}`
}

function introDismissedStorageKey(userId: string): string {
    return `${INTRO_DISMISSED_STORAGE_PREFIX}:${userId}`
}

/**
 * True once this user has closed the intro popover. An unknown user reports
 * not dismissed so the check can run again once auth resolves. Unreadable
 * storage reports dismissed so the prompt never nags in a loop.
 */
function readIntroDismissed(userId: string | undefined): boolean {
    if (!userId) return false
    if (typeof window === 'undefined') return true
    try {
        return (
            window.localStorage.getItem(introDismissedStorageKey(userId)) ===
            'true'
        )
    } catch {
        return true
    }
}

function writeIntroDismissed(userId: string | undefined): void {
    if (!userId || typeof window === 'undefined') return
    try {
        window.localStorage.setItem(introDismissedStorageKey(userId), 'true')
    } catch {
        // Private mode / quota — the in-memory flag still hides it this load.
    }
}

/** Unreadable storage reports "asked" so the prompt never nags in a loop. */
function readHasAskedQuestion(userId: string): boolean {
    if (typeof window === 'undefined') return true
    try {
        return (
            window.localStorage.getItem(hasAskedStorageKey(userId)) === 'true'
        )
    } catch {
        return true
    }
}

function writeHasAskedQuestion(userId: string): void {
    if (typeof window === 'undefined') return
    try {
        window.localStorage.setItem(hasAskedStorageKey(userId), 'true')
    } catch {
        // Private mode / quota — the server lookup still hides the prompt.
    }
}
